import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from '../hooks/use-i18n'
import type { Locale } from '../hooks/use-i18n'
import { ChevronRight } from 'lucide-react'

const LOCALES: Locale[] = ['en', 'fr', 'es', 'ru', 'it', 'de', 'pt', 'hy']

interface LanguageSelectorProps {
  variant?: 'icon' | 'row'
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ variant = 'icon' }) => {
  const { t, locale, setLocale, flags, localeNames } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const picker = open ? (
    <div
      className={`absolute z-50 py-1 min-w-[180px] rounded-lg bg-popover text-popover-foreground border border-border shadow-lg ${
        variant === 'row' ? 'left-0 right-0 top-full mt-2' : 'right-0 top-full mt-1'
      }`}
    >
      {LOCALES.map((loc) => (
        <button
          key={loc}
          type="button"
          onClick={() => {
            setLocale(loc)
            setOpen(false)
          }}
          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-small hover:bg-muted/50 ${
            locale === loc ? 'bg-muted/30 font-medium' : ''
          }`}
        >
          <span>{flags[loc]}</span>
          <span>{localeNames[loc]}</span>
        </button>
      ))}
    </div>
  ) : null

  if (variant === 'row') {
    return (
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-3 rounded-2xl border border-border bg-card/60 hover:bg-muted/20 transition-colors p-4 text-left"
        >
          <div className="w-11 h-11 rounded-xl bg-muted/40 flex items-center justify-center text-2xl leading-none">
            {flags[locale]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-foreground">{t('common.language')}</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {flags[locale]} {localeNames[locale]}
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
        </button>
        {picker}
      </div>
    )
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 md:p-2 rounded-lg hover:bg-muted/30 text-muted-foreground hover:text-foreground transition-colors"
        aria-label={localeNames[locale]}
        title={localeNames[locale]}
      >
        <span className="text-base md:text-lg leading-none">{flags[locale]}</span>
      </button>
      {picker}
    </div>
  )
}

export default LanguageSelector
